import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authContext } from '../../context/index.js';
import Button from '../../components/Button.jsx';
import Input from '../../components/Input.jsx';
import Card from '../../components/Card.jsx';
import { GraduationCap, Eye, EyeOff } from 'lucide-react';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'student'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { register } = useContext(authContext);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await register(
        formData.name,
        formData.email,
        formData.password,
        formData.role
      );
      
      if (result.success) {
        console.log("Register successful, result:", result);
        console.log("UserData:", result.userData);
        console.log("FormData role:", formData.role);
        
        // Determine role and navigate
        const userRole = (result.userData?.role || formData.role || 'student').toString().toLowerCase();
        const targetPath = `/${userRole}/dashboard`;
        console.log("Final userRole:", userRole);
        console.log("Redirecting to:", targetPath);

        // SPA navigate first
        navigate(targetPath, { replace: true });

        // Fallback hard redirect in case SPA navigation is blocked
        setTimeout(() => {
          if (window.location.pathname !== targetPath) {
            window.location.assign(targetPath);
          }
        }, 100);
      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (err) {
      setError('An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50">
      {/* Header with Home Button */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-accent-600 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-secondary-900">SkillForge</span>
            </Link>
            <Link
              to="/"
              className="text-secondary-600 hover:text-secondary-900 px-3 py-2 rounded-md text-sm font-medium transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="flex items-center justify-center p-4 min-h-[calc(100vh-4rem)]">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl mb-4">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-secondary-900">SkillForge</h1>
            <p className="text-secondary-600 mt-2">Join our learning community</p>
          </div>

        <Card>
          <Card.Header>
            <Card.Title>Create Account</Card.Title>
            <Card.Description>
              Choose your role and start your learning journey
            </Card.Description>
          </Card.Header>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <Input
              label="Full Name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />

            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
            />

            <div>
              <label className="label text-secondary-700 mb-2 block">Role</label>
              <div className="grid grid-cols-2 gap-3">
                <label className={`
                  flex items-center justify-center p-3 border rounded-lg cursor-pointer transition-colors
                  ${formData.role === 'student' 
                    ? 'border-primary-600 bg-primary-50 text-primary-700' 
                    : 'border-secondary-300 hover:border-secondary-400'
                  }
                `}>
                  <input
                    type="radio"
                    name="role"
                    value="student"
                    checked={formData.role === 'student'}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="text-sm font-medium">Student</div>
                    <div className="text-xs text-secondary-500">Learn courses</div>
                  </div>
                </label>
                <label className={`
                  flex items-center justify-center p-3 border rounded-lg cursor-pointer transition-colors
                  ${formData.role === 'teacher' 
                    ? 'border-primary-600 bg-primary-50 text-primary-700' 
                    : 'border-secondary-300 hover:border-secondary-400'
                  }
                `}>
                  <input
                    type="radio"
                    name="role"
                    value="teacher"
                    checked={formData.role === 'teacher'}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="text-sm font-medium">Teacher</div>
                    <div className="text-xs text-secondary-500">Create courses</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-8 text-secondary-400 hover:text-secondary-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                required
                className="mt-1 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <label className="ml-2 text-sm text-secondary-600">
                I agree to the{' '}
                <Link to="/terms" className="text-primary-600 hover:text-primary-700">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary-600 hover:text-primary-700">
                  Privacy Policy
                </Link>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <Card.Footer className="text-center">
            <p className="text-sm text-secondary-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Sign in
              </Link>
            </p>
          </Card.Footer>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default Register;
